import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  COUNCIL_AREAS,
  invites as invitesApi,
  ROLE_LABELS,
  units as unitsApi,
  type CouncilArea,
  type InviteRow,
  type Member,
  type Role,
} from '../api';
import { useAuth } from '../auth';
import { useUnits } from '../units';
import { InviteLink } from '../components/InviteLink';

const ALL_ROLES: Role[] = ['ADMIN', 'DEN_LEADER', 'PARENT', 'PACK_COMMITTEE_FINANCE', 'COUNCIL'];

function ExportButton({ unitId, unitName }: { unitId: string; unitName: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setError(null);
    setBusy(true);
    try {
      const json = await unitsApi.exportBundle(unitId);
      const slug = unitName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'unit';
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span>
      <button onClick={run} disabled={busy}>
        {busy ? 'Exporting…' : 'Export unit data (JSON)'}
      </button>
      {error && <span className="error"> {error}</span>}
    </span>
  );
}

export function Members() {
  const { unitId = '' } = useParams();
  const { me } = useAuth();
  const { units, currentUnit } = useUnits();

  const mayAdminister = useMemo(
    () => me?.isPlatformAdmin || units.find((u) => u.id === unitId)?.roles.includes('ADMIN'),
    [me, units, unitId],
  );

  const [members, setMembers] = useState<Member[] | null>(null);
  const [pending, setPending] = useState<InviteRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    unitsApi
      .members(unitId)
      .then(setMembers)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load members'));
    invitesApi
      .list(unitId)
      .then(setPending)
      .catch(() => setPending([]));
  }, [unitId]);

  useEffect(() => {
    if (mayAdminister) load();
  }, [mayAdminister, load]);

  if (!mayAdminister) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Members</h1>
        <ExportButton unitId={unitId} unitName={currentUnit?.name ?? 'unit'} />
      </div>
      <p className="muted">{currentUnit?.name ?? unitId}</p>
      {error && <p className="error">{error}</p>}

      <AddMemberForm unitId={unitId} onAdded={load} />

      {pending.length > 0 && (
        <>
          <h2>Pending invites</h2>
          <table className="grid">
            <thead>
              <tr>
                <th>Email</th>
                <th>Roles</th>
                <th>Expires</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pending.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td>{inv.roles.map((r) => ROLE_LABELS[r]).join(', ')}</td>
                  <td className="muted">{new Date(inv.expiresAt).toLocaleDateString()}</td>
                  <td className="row-actions">
                    <InviteLink url={inv.acceptUrl} label="" />
                    <button
                      onClick={async () => {
                        await invitesApi.revoke(unitId, inv.id);
                        load();
                      }}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <table className="grid">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Roles</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {members?.map((m) => (
            <MemberRow
              key={m.userId}
              unitId={unitId}
              member={m}
              isSelf={m.userId === me?.user.id}
              onChanged={load}
            />
          ))}
          {members?.length === 0 && (
            <tr>
              <td colSpan={4} className="muted">
                No members yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AddMemberForm({ unitId, onAdded }: { unitId: string; onAdded: () => void }) {
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<Role[]>(['PARENT']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitedUrl, setInvitedUrl] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInvitedUrl(null);
    setBusy(true);
    try {
      const res = await unitsApi.addMember(unitId, { email, roles });
      setEmail('');
      setRoles(['PARENT']);
      if (res.invited) setInvitedUrl(res.acceptUrl);
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add member');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-member" onSubmit={submit}>
      <input
        type="email"
        placeholder="person@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <RolePicker value={roles} onChange={setRoles} />
      <button type="submit" className="primary" disabled={busy || roles.length === 0}>
        {busy ? '…' : 'Add'}
      </button>
      {invitedUrl && (
        <InviteLink url={invitedUrl} label="No account for that email yet — send them this invite link:" />
      )}
      {error && <span className="error">{error}</span>}
      <p className="hint">The person must already have an account (email invites land in SCO-15).</p>
    </form>
  );
}

function MemberRow({
  unitId,
  member,
  isSelf,
  onChanged,
}: {
  unitId: string;
  member: Member;
  isSelf: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [roles, setRoles] = useState<Role[]>(member.roles);
  const [areas, setAreas] = useState<CouncilArea[]>(member.councilViewAreas);
  const [canMessage, setCanMessage] = useState(member.councilCanMessage);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    setBusy(true);
    try {
      await unitsApi.setRoles(unitId, member.userId, {
        roles,
        councilViewAreas: roles.includes('COUNCIL') ? areas : [],
        councilCanMessage: roles.includes('COUNCIL') ? canMessage : false,
      });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Remove ${member.email} from this unit?`)) return;
    setBusy(true);
    try {
      await unitsApi.removeMember(unitId, member.userId);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove');
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>{member.displayName}</td>
      <td>{member.email}</td>
      <td>
        {editing ? (
          <>
            <RolePicker value={roles} onChange={setRoles} />
            {roles.includes('COUNCIL') && (
              <CouncilGrantPicker
                areas={areas}
                canMessage={canMessage}
                onAreas={setAreas}
                onCanMessage={setCanMessage}
              />
            )}
          </>
        ) : (
          <>
            {member.roles.map((r) => ROLE_LABELS[r]).join(', ')}
            {member.roles.includes('COUNCIL') && (
              <div className="muted small">
                Council view:{' '}
                {member.councilViewAreas.length ? member.councilViewAreas.join(', ') : 'none'}
                {member.councilCanMessage && ' · can message'}
              </div>
            )}
          </>
        )}
        {error && <div className="error">{error}</div>}
      </td>
      <td className="row-actions">
        {editing ? (
          <>
            <button className="primary" onClick={save} disabled={busy || roles.length === 0}>
              Save
            </button>
            <button
              onClick={() => {
                setRoles(member.roles);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)}>Edit</button>
            <button onClick={remove} disabled={busy || isSelf} title={isSelf ? 'You can’t remove yourself' : ''}>
              Remove
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

function CouncilGrantPicker({
  areas,
  canMessage,
  onAreas,
  onCanMessage,
}: {
  areas: CouncilArea[];
  canMessage: boolean;
  onAreas: (a: CouncilArea[]) => void;
  onCanMessage: (b: boolean) => void;
}) {
  return (
    <div className="role-picker" style={{ marginTop: 6 }}>
      <span className="muted small">Council may view:</span>
      {COUNCIL_AREAS.map((a) => (
        <label key={a}>
          <input
            type="checkbox"
            checked={areas.includes(a)}
            onChange={(e) => onAreas(e.target.checked ? [...areas, a] : areas.filter((x) => x !== a))}
          />
          {a}
        </label>
      ))}
      <label>
        <input type="checkbox" checked={canMessage} onChange={(e) => onCanMessage(e.target.checked)} />
        can send messages &amp; manage announcements
      </label>
    </div>
  );
}

function RolePicker({ value, onChange }: { value: Role[]; onChange: (r: Role[]) => void }) {
  return (
    <span className="role-picker">
      {ALL_ROLES.map((r) => (
        <label key={r}>
          <input
            type="checkbox"
            checked={value.includes(r)}
            onChange={(e) =>
              onChange(e.target.checked ? [...value, r] : value.filter((x) => x !== r))
            }
          />
          {ROLE_LABELS[r]}
        </label>
      ))}
    </span>
  );
}
